import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';

import { useEventTracking, useInsight } from '@hooks';
import { subscriber_directories_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import styles from './styles.module.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface InsightSectionProps {
  title: string;
  relationshipSource: string;
  relationshipType: string;
  icon: string;
  searchAttribute: string;
  expand?: boolean;
}

const FEWER_RESULTS = 'Fewer Results';
const MORE_RESULTS = 'More Results';

const InsightSection = ({
  title,
  relationshipSource,
  relationshipType,
  icon,
  searchAttribute,
  expand = false,
}: InsightSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(expand);
  const limit = isExpanded ? 20 : 5;
  const { insight, isLoadingInsight } = useInsight({
    relationshipSource: relationshipSource,
    relationshipType: relationshipType,
    limit: limit,
  });
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.InsightSection,
  });

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => {
            return value + '%';
          },
        },
      },
    },
    maxBarThickness: 10,
  };

  const labels = [...Array(limit)].map((_, i) => {
    return i + 1;
  });
  const bgColors = isExpanded
    ? [
        '#EC635E',
        '#C63165',
        '#941D6B',
        '#590D6A',
        '#00005E',
        '#232784',
        '#404DAB',
        '#5D74D1',
        '#7C9EF8',
        '#896EBF',
        '#EC635E',
        '#C63165',
        '#941D6B',
        '#590D6A',
        '#00005E',
        '#232784',
        '#404DAB',
        '#5D74D1',
        '#7C9EF8',
        '#896EBF',
      ]
    : 'black';

  const data = {
    labels,
    datasets: [
      {
        label: title,
        data: isLoadingInsight
          ? []
          : insight.top_results.map((result) =>
              ((result[1] / insight.total_count) * 100).toFixed(2)
            ),
        backgroundColor: bgColors,
      },
    ],
  };

  const expandButtonText = isExpanded ? FEWER_RESULTS : MORE_RESULTS;

  const handleExpandClick = () => {
    trackEvent(
      click({ clickTarget: ClickTarget.ExpandButton, clickText: `${expandButtonText}: ${title}` })
    );
    setIsExpanded((expandedStatus) => !expandedStatus);
  };

  return (
    <Col className={isExpanded ? 'col-md-12' : 'col-md-6'}>
      <div
        className={clsx(
          styles.insightContainer,
          isExpanded ? styles.insightContainerExpanded : styles.insightContainerMin
        )}
      >
        <h3 className={styles.headerTextContainer}>
          <span className={styles.headerTextSpan}>
            <img src={icon} className={styles.titleIcon} role="presentation" />
            <span className={styles.headerText}>{title}</span>
          </span>
          <span className={styles.headerTextBtnContainer}>
            {isExpanded ? '< ' : ''}
            <button className={styles.headerTextBtn} onClick={handleExpandClick}>
              {expandButtonText}
            </button>
            {isExpanded ? '' : ' >'}
          </span>
        </h3>
        {isLoadingInsight ? (
          <p>Loading…</p>
        ) : (
          <Row>
            <Col className={isExpanded ? 'col-md-6' : 'col-md-12'}>
              <ol className={styles.rankedList}>
                {insight.top_results.map((result, i) => {
                  const resultName = result[0];
                  const directorySearchPath = subscriber_directories_path({
                    [searchAttribute]: resultName,
                  });

                  const handleResultClick = () => {
                    trackEvent(
                      click({
                        clickLink: directorySearchPath,
                        clickTarget: ClickTarget.DirectoryProfileInterest,
                        clickText: resultName,
                      })
                    );
                  };

                  return (
                    <li key={resultName} className={styles.rankedListItem}>
                      <a href={directorySearchPath} onClickCapture={handleResultClick}>
                        <span>
                          {i + 1}. {resultName}
                        </span>
                      </a>
                      <span className={styles.rankedListItemDetails}>
                        {isExpanded ? (
                          <span
                            className={styles.rankedListItemColorIndicator}
                            style={{ backgroundColor: bgColors[i] }}
                          />
                        ) : (
                          <span />
                        )}

                        <span>{((result[1] / insight.total_count) * 100).toFixed(0)}%</span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            </Col>
            <Col className={isExpanded ? 'col-md-6' : 'col-md-12'}>
              <Bar options={options} data={data} />
            </Col>
          </Row>
        )}
      </div>
    </Col>
  );
};

export default InsightSection;
